import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, ShoppingCart, IndianRupee, ArrowUpRight, Eye, TrendingUp, Calendar, Clock, Award, BarChart3, PieChart, AlertCircle, Store, ChevronDown, Plus, X, MapPin, Phone, Building2, Upload, CheckCircle2, ShieldAlert } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { usePermissions } from "../context/PermissionsContext";
import { dashboardAPI, storeAPI } from "../api/apiService";
import Layout from "../components/Layout";
import { useSubscription } from "../context/SubscriptionContext";
import ExpiryOverlay from "../components/Owner/ExpiryOverlay";

const StatCard = ({ icon: Icon, title, value, change, color }) => (
  <motion.div
    whileHover={{ y: -8, scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    className="relative overflow-hidden rounded-xl p-6 h-40 flex flex-col group cursor-pointer"
  >
    {/* Color Background - Solid Bottom Section */}
    <div className={`absolute bottom-0 left-0 right-0 h-1 ${color}`} />
    <div className={`absolute top-0 right-0 w-24 h-24 ${color} opacity-5 rounded-full blur-2xl`} />

    {/* Card Background */}
    <div className="absolute inset-0 bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 border border-gray-200 dark:border-gray-700" />

    {/* Content */}
    <div className="relative z-10 flex items-start justify-between h-full flex-col">
      <div className="w-full flex items-center justify-between">
        <p className="text-base font-bold text-gray-900 dark:text-white">{title}</p>
        <motion.div
          whileHover={{ scale: 1.1, rotate: 10 }}
          className={`p-2 rounded-lg ${color} shadow-sm flex items-center justify-center -mr-2`}
        >
          {typeof Icon === 'string' ? (
            <img src={Icon} alt={title} className="w-6 h-6 object-contain" />
          ) : (
            <Icon className="w-5 h-5 text-gray-700 dark:text-gray-200" />
          )}
        </motion.div>
      </div>

      <div className="flex-1 flex flex-col justify-end w-full">
        <h3 className="text-3xl font-bold text-black dark:text-white mb-2">
          {value}
        </h3>
        <div className="flex items-center gap-1">
          <ArrowUpRight className={`w-4 h-4 font-bold ${change >= 0 ? 'text-green-500' : 'text-red-500'}`} />
          <span className={`text-xs font-semibold ${change >= 0 ? 'text-green-500' : 'text-red-500'}`}>
            {Math.abs(change)}%
          </span>
        </div>
      </div>
    </div>
  </motion.div>
);

export default function OwnerDashboardPage() {
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const { hasPermission } = usePermissions();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backendConnected, setBackendConnected] = useState(false);
  const [stats, setStats] = useState([
    { title: "Total Customers", value: "0", change: 0, icon: "https://cdn-icons-png.flaticon.com/512/11478/11478510.png", color: "bg-[#EBFDF5]" },
    { title: "Total Products", value: "0", change: 0, icon: "https://cdn-icons-png.flaticon.com/512/13138/13138387.png", color: "bg-[#E1EAFF]" },
    { title: "Total Revenue", value: "₹0", change: 0, icon: "https://cdn-icons-png.flaticon.com/512/17995/17995148.png", color: "bg-[#FFF9E5]" },
    { title: "Active Customers", value: "0", change: 0, icon: "https://cdn-icons-png.flaticon.com/512/9759/9759919.png", color: "bg-[#F3E8FF]" },
  ]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [todayStats, setTodayStats] = useState({ invoices: 0, revenue: 0, transactions: 0 });
  const { getSubscriptionStatus } = useSubscription();

  // ─── Multi-Store State ──────────────────────────────────────────
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null); // null = All Stores
  const [storeDropdownOpen, setStoreDropdownOpen] = useState(false);
  const [showAddStoreModal, setShowAddStoreModal] = useState(false);
  const [newStore, setNewStore] = useState({ name: '', city: '', address: '', phone: '' });
  const [addingStore, setAddingStore] = useState(false);
  
  // Unified UPI QR Configuration State
  const [showUpiModal, setShowUpiModal] = useState(false);
  const [upiScope, setUpiScope] = useState('ALL'); // 'ALL' | 'SINGLE'
  const [upiSelectedStoreId, setUpiSelectedStoreId] = useState(null);
  const [upiData, setUpiData] = useState({ upi_id: '', upi_qr_image_url: '', account_holder_name: '', bank_name: '' });
  const [upiErrors, setUpiErrors] = useState({});
  const [savingUpi, setSavingUpi] = useState(false);
  const [confirmDialogType, setConfirmDialogType] = useState(null); // null | 'ALL' | 'SINGLE'
  const [bulkSuccessMsg, setBulkSuccessMsg] = useState('');

  const subStatus = getSubscriptionStatus('OWNER');
  const isExpired = subStatus.status === 'EXPIRED';
  const isExpiringSoon = subStatus.status === 'EXPIRING_SOON';

  // Fetch stores list
  useEffect(() => {
    fetchStores();
  }, []);

  // Re-fetch dashboard when selected store changes
  useEffect(() => {
    fetchDashboardData();
  }, [selectedStore]);

  async function fetchStores() {
    try {
      const res = await storeAPI.getAll();
      setStores(res.data || []);
    } catch (err) {
      console.warn('Failed to load stores:', err);
    }
  }

  async function handleAddStore() {
    if (!newStore.name) return;
    setAddingStore(true);
    try {
      await storeAPI.create(newStore);
      setShowAddStoreModal(false);
      setNewStore({ name: '', city: '', address: '', phone: '' });
      fetchStores();
    } catch (error) {
      console.error(error);
    } finally {
      setAddingStore(false);
    }
  }

  // ── Unified UPI QR Handlers ─────────────────────────────────────────
  const handleOpenUpiModal = (scope = 'ALL', storeId = null) => {
    const targetStoreId = storeId || selectedStore || (stores[0]?.id || null);
    setUpiScope(scope);
    setUpiSelectedStoreId(targetStoreId);
    setUpiErrors({});

    if (scope === 'SINGLE' && targetStoreId) {
      const store = stores.find(s => s.id === Number(targetStoreId));
      if (store) {
        setUpiData({
          upi_id: store.upi_id || '',
          upi_qr_image_url: store.upi_qr_image_url || '',
          account_holder_name: store.account_holder_name || '',
          bank_name: store.bank_name || ''
        });
      } else {
        setUpiData({ upi_id: '', upi_qr_image_url: '', account_holder_name: '', bank_name: '' });
      }
    } else {
      setUpiData({ upi_id: '', upi_qr_image_url: '', account_holder_name: '', bank_name: '' });
    }

    setShowUpiModal(true);
    setStoreDropdownOpen(false);
  };

  const handleScopeChange = (newScope) => {
    setUpiScope(newScope);
    setUpiErrors({});
    if (newScope === 'SINGLE') {
      const targetId = upiSelectedStoreId || selectedStore || (stores[0]?.id || null);
      setUpiSelectedStoreId(targetId);
      if (targetId) {
        const store = stores.find(s => s.id === Number(targetId));
        if (store) {
          setUpiData({
            upi_id: store.upi_id || '',
            upi_qr_image_url: store.upi_qr_image_url || '',
            account_holder_name: store.account_holder_name || '',
            bank_name: store.bank_name || ''
          });
        }
      }
    }
  };

  const handleTargetStoreChange = (storeId) => {
    setUpiSelectedStoreId(storeId);
    setUpiErrors({});
    const store = stores.find(s => s.id === Number(storeId));
    if (store) {
      setUpiData({
        upi_id: store.upi_id || '',
        upi_qr_image_url: store.upi_qr_image_url || '',
        account_holder_name: store.account_holder_name || '',
        bank_name: store.bank_name || ''
      });
    }
  };

  const handleQrFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!allowed.includes(file.type)) {
      setUpiErrors(prev => ({ ...prev, qrFile: 'Only PNG, JPG, and JPEG formats are allowed.' }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUpiErrors(prev => ({ ...prev, qrFile: 'File size must not exceed 5 MB.' }));
      return;
    }
    setUpiErrors(prev => ({ ...prev, qrFile: undefined }));
    const reader = new FileReader();
    reader.onloadend = () => {
      setUpiData(prev => ({ ...prev, upi_qr_image_url: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const validateUpiData = () => {
    const errs = {};
    if (!upiData.upi_id.trim()) errs.upi_id = 'UPI ID is required.';
    if (!upiData.account_holder_name.trim()) errs.account_holder_name = 'Account Holder Name is required.';
    if (upiScope === 'SINGLE' && !upiSelectedStoreId) errs.store_id = 'Target store is required.';
    return errs;
  };

  const handleSaveUpiClick = () => {
    const errs = validateUpiData();
    if (Object.keys(errs).length > 0) {
      setUpiErrors(errs);
      return;
    }
    setUpiErrors({});
    if (upiScope === 'ALL') {
      setConfirmDialogType('ALL');
    } else {
      setConfirmDialogType('SINGLE');
    }
  };

  const handleConfirmSaveUpi = async () => {
    setSavingUpi(true);
    setConfirmDialogType(null);
    try {
      if (upiScope === 'ALL') {
        const res = await storeAPI.bulkApplyUpiQr({
          upi_id: upiData.upi_id,
          account_holder_name: upiData.account_holder_name,
          bank_name: upiData.bank_name,
          upi_qr_image_url: upiData.upi_qr_image_url,
        });
        setShowUpiModal(false);
        fetchStores();
        setBulkSuccessMsg(res.data?.message || 'UPI QR configuration has been successfully applied to all stores.');
        setTimeout(() => setBulkSuccessMsg(''), 5000);
      } else {
        await storeAPI.update(upiSelectedStoreId, {
          upi_id: upiData.upi_id,
          account_holder_name: upiData.account_holder_name,
          bank_name: upiData.bank_name,
          upi_qr_image_url: upiData.upi_qr_image_url,
        });
        setShowUpiModal(false);
        fetchStores();
        const targetStore = stores.find(s => s.id === Number(upiSelectedStoreId));
        setBulkSuccessMsg(`UPI QR configuration has been successfully applied to ${targetStore?.name || 'the selected store'}.`);
        setTimeout(() => setBulkSuccessMsg(''), 5000);
      }
    } catch (error) {
      console.error('UPI configuration error:', error);
      const serverErrors = error?.response?.data?.errors;
      if (serverErrors) {
        setUpiErrors(serverErrors);
      }
    } finally {
      setSavingUpi(false);
    }
  };

  const selectedStoreName = selectedStore
    ? stores.find(s => s.id === selectedStore)?.name || 'Store'
    : 'All Stores';

  const calculateTotalRevenue = (customersData) => {
    const total = customersData.reduce((sum, customer) => {
      const spent = customer.spent || "₹0";
      const amount = parseInt(spent.replace(/[^0-9]/g, "")) || 0;
      return sum + amount;
    }, 0);

    if (total >= 10000000) {
      return `₹${(total / 10000000).toFixed(1)}Cr`;
    } else if (total >= 100000) {
      return `₹${(total / 100000).toFixed(1)}L`;
    }
    return `₹${total.toLocaleString()}`;
  };

  async function fetchDashboardData() {
    setLoading(true);
    try {
      setBackendConnected(false);

      // Fetch Overview and Recent Transactions (filtered by selected store)
      const sid = selectedStore || undefined;
      const [overviewRes, recentRes, analyticsRes] = await Promise.all([
        dashboardAPI.getOverview(sid),
        dashboardAPI.getRecentTransactions(5, sid),
        dashboardAPI.getAnalytics(undefined, sid)
      ]);

      if (overviewRes.data) {
        setDashboardData(overviewRes.data);
        setBackendConnected(true);

        // Extract data
        const data = overviewRes.data;

        setStats([
          {
            title: "Total Customers",
            value: String(data.total_customers || 0),
            change: 12,
            icon: "https://cdn-icons-png.flaticon.com/512/11478/11478510.png",
            color: "bg-[#EBFDF5]"
          },
          {
            title: "Total Products",
            value: String(data.total_products || 0),
            change: 0,
            icon: "https://cdn-icons-png.flaticon.com/512/13138/13138387.png",
            color: "bg-[#E1EAFF]"
          },
          {
            title: "Total Revenue",
            value: `₹${(data.total_revenue || 0).toLocaleString()}`,
            change: 8,
            icon: "https://cdn-icons-png.flaticon.com/512/17995/17995148.png",
            color: "bg-[#FFF9E5]"
          },
          {
            title: "Active Customers",
            value: String(data.active_customers || 0),
            change: 5,
            icon: "https://cdn-icons-png.flaticon.com/512/9759/9759919.png",
            color: "bg-[#F3E8FF]"
          },
        ]);
      }

      if (recentRes.data) {
        // Transform backend data to match UI expected format
        const orders = recentRes.data.map(order => ({
          id: order.invoice_number,
          customer: order.customer__name || `Customer #${order.id}`,
          amount: `₹${order.total_amount}`,
          status: order.payment_status === 'paid' ? 'Completed' : 'Pending'
        }));
        setRecentOrders(orders);
      }

      setLoading(false);
    } catch (error) {
      setBackendConnected(false);
    } finally {
      setLoading(false);
    }
  }
  const handleCreateInvoice = () => {
    navigate("/pos-billing");
  };

  const handleAddProduct = () => {
    navigate("/inventory");
  };

  const handleNewCustomer = () => {
    navigate("/customers");
  };

  const handleViewReports = () => {
    navigate("/reports");
  };

  const handleViewInvoices = () => {
    navigate("/invoices");
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
    },
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-100 via-purple-50 to-slate-100 dark:from-dark-bg dark:via-dark-bg dark:to-dark-bg transition-colors">
        {/* Expiry Banner */}
        {isExpired && (
          <div className="bg-red-600 text-white text-center py-3 px-4 flex items-center justify-center gap-3 sticky top-0 z-50 animate-pulse">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="font-bold text-sm tracking-wide">🔴 Subscription Expired – Upgrade to continue services.</span>
            <button
              onClick={() => navigate("/subscriptions")}
              className="ml-4 px-3 py-1 bg-white text-red-600 rounded-lg text-xs font-black hover:bg-red-50 transition-colors uppercase"
            >
              Upgrade Now
            </button>
          </div>
        )}

        {isExpiringSoon && !isExpired && (
          <div className="bg-yellow-500 text-slate-900 text-center py-2 px-4 flex items-center justify-center gap-3 sticky top-0 z-50">
            <AlertCircle className="w-4 h-4" />
            <span className="font-bold text-xs uppercase tracking-wider">{subStatus.message} – Renew soon to avoid interruption</span>
          </div>
        )}

        <div className="p-4 md:p-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-xl bg-white flex items-center justify-center shadow-lg border border-gray-100 overflow-hidden group">
                <img src="https://cdn-icons-png.flaticon.com/512/2329/2329087.png" alt="Dashboard" className="w-10 h-10 object-contain group-hover:scale-110 transition-transform duration-200" />
              </div>
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">Dashboard</h1>
                <p className="text-gray-600 dark:text-gray-300 text-sm">Billing & Sales Overview</p>
              </div>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-lg">Welcome back! Here's your business overview</p>
          </motion.div>

          {/* ─── Store Selector Bar ─── */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-8 flex flex-wrap items-center gap-3"
          >
            {/* Dropdown trigger */}
            <div className="relative">
              <button
                onClick={() => setStoreDropdownOpen(!storeDropdownOpen)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border shadow-sm hover:shadow-md transition-all font-semibold text-gray-800 dark:text-white min-w-[200px] justify-between"
              >
                <span className="flex items-center gap-2">
                  <Store className="w-4 h-4 text-purple-500" />
                  {selectedStoreName}
                </span>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${storeDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {storeDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -8, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full left-0 mt-2 w-80 sm:w-96 bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border rounded-xl shadow-xl z-50 overflow-hidden"
                  >
                    {/* All Stores option */}
                    <button
                      onClick={() => { setSelectedStore(null); setStoreDropdownOpen(false); }}
                      className={`w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-white/5 transition-colors ${
                        !selectedStore ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 font-bold' : 'text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Building2 className="w-4 h-4" />
                        <span>All Stores (Consolidated)</span>
                      </div>
                      {!selectedStore && (
                        <span className="text-[10px] uppercase tracking-wider font-extrabold px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/60 text-purple-700 dark:text-purple-300">
                          Active
                        </span>
                      )}
                    </button>

                    {stores.length > 0 && (
                      <div className="border-t border-gray-100 dark:border-dark-border">
                        {stores.map(store => (
                          <button
                            key={store.id}
                            onClick={() => { setSelectedStore(store.id); setStoreDropdownOpen(false); }}
                            className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-white/5 transition-colors ${
                              selectedStore === store.id ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 font-bold' : 'text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            <MapPin className="w-4 h-4 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="font-semibold truncate">{store.name}</p>
                              {store.city && <p className="text-xs text-gray-400 truncate">{store.city}</p>}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Add Store button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowAddStoreModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 min-h-[42px] rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold shadow-sm hover:shadow-md transition-all text-sm"
            >
              <Plus className="w-4 h-4" />
              Add Store
            </motion.button>
            
            {/* Manage UPI / Configure All Stores QR button */}
            {selectedStore ? (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleOpenUpiModal('SINGLE', selectedStore)}
                className="flex items-center gap-2 px-4 py-2.5 min-h-[42px] rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold shadow-sm hover:shadow-md transition-all text-sm sm:ml-auto"
              >
                <IndianRupee className="w-4 h-4" />
                Manage Store UPI
              </motion.button>
            ) : (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleOpenUpiModal('ALL')}
                title="Configure the default UPI QR code that will be applied to all stores."
                className="flex items-center gap-2 px-4 py-2.5 min-h-[44px] rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold shadow-md hover:shadow-lg transition-all text-sm sm:ml-auto"
              >
                <IndianRupee className="w-4 h-4" />
                <span>Configure All Stores QR</span>
              </motion.button>
            )}
          </motion.div>

          {/* ─── Add Store Modal ─── */}
          <AnimatePresence>
            {showAddStoreModal && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
                onClick={() => setShowAddStoreModal(false)}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0, y: 20 }}
                  animate={{ scale: 1, opacity: 1, y: 0 }}
                  exit={{ scale: 0.9, opacity: 0, y: 20 }}
                  onClick={e => e.stopPropagation()}
                  className="w-full max-w-md bg-white dark:bg-dark-card rounded-2xl border border-gray-200 dark:border-dark-border shadow-2xl"
                >
                  {/* Header */}
                  <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-dark-border">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center">
                        <Store className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">Add New Store</h3>
                    </div>
                    <button onClick={() => setShowAddStoreModal(false)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 transition">
                      <X className="w-5 h-5 text-gray-400" />
                    </button>
                  </div>

                  {/* Form */}
                  <div className="p-6 space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 dark:text-gray-300 mb-1.5">Store Name *</label>
                      <input
                        value={newStore.name}
                        onChange={e => setNewStore({ ...newStore, name: e.target.value })}
                        placeholder="e.g. Downtown Branch"
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 dark:text-gray-300 mb-1.5">City</label>
                      <input
                        value={newStore.city}
                        onChange={e => setNewStore({ ...newStore, city: e.target.value })}
                        placeholder="e.g. Mumbai"
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 dark:text-gray-300 mb-1.5">Address</label>
                      <input
                        value={newStore.address}
                        onChange={e => setNewStore({ ...newStore, address: e.target.value })}
                        placeholder="e.g. 12, MG Road"
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 dark:text-gray-300 mb-1.5">Phone</label>
                      <input
                        value={newStore.phone}
                        onChange={e => setNewStore({ ...newStore, phone: e.target.value })}
                        placeholder="e.g. 9876543210"
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition"
                      />
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center gap-3 p-6 border-t border-gray-100 dark:border-dark-border">
                    <button
                      onClick={() => setShowAddStoreModal(false)}
                      className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-border text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-white/5 transition"
                    >
                      Cancel
                    </button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleAddStore}
                      disabled={addingStore || !newStore.name.trim()}
                      className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold shadow-sm hover:shadow-md transition-all disabled:opacity-50"
                    >
                      {addingStore ? 'Creating...' : 'Create Store'}
                    </motion.button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ─── Unified UPI QR Config Modal ─── */}
          <AnimatePresence>
            {showUpiModal && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
                onClick={() => { if (!confirmDialogType) setShowUpiModal(false); }}
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0, y: 20 }}
                  animate={{ scale: 1, opacity: 1, y: 0 }}
                  exit={{ scale: 0.9, opacity: 0, y: 20 }}
                  onClick={e => e.stopPropagation()}
                  className="w-full max-w-md bg-white dark:bg-dark-card rounded-2xl border border-gray-200 dark:border-dark-border shadow-2xl max-h-[90vh] overflow-y-auto"
                >
                  {/* Header */}
                  <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-dark-border">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center">
                        <IndianRupee className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white">UPI QR Config</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Configure UPI payment details & QR</p>
                      </div>
                    </div>
                    <button onClick={() => setShowUpiModal(false)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 transition">
                      <X className="w-5 h-5 text-gray-400" />
                    </button>
                  </div>

                  {/* Body */}
                  <div className="p-6 space-y-4">
                    {/* Scope Selector */}
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                        Configuration Scope <span className="text-red-500">*</span>
                      </label>
                      <div className="grid grid-cols-2 gap-2 p-1 bg-gray-100 dark:bg-dark-bg rounded-xl">
                        <button
                          type="button"
                          id="scope-all-stores-btn"
                          onClick={() => handleScopeChange('ALL')}
                          className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-xs sm:text-sm font-bold transition-all ${
                            upiScope === 'ALL'
                              ? 'bg-white dark:bg-dark-card text-purple-700 dark:text-purple-300 shadow-sm'
                              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                          }`}
                        >
                          <Building2 className="w-4 h-4 flex-shrink-0" />
                          <span>All Stores (Default)</span>
                        </button>
                        <button
                          type="button"
                          id="scope-selected-store-btn"
                          onClick={() => handleScopeChange('SINGLE')}
                          className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-xs sm:text-sm font-bold transition-all ${
                            upiScope === 'SINGLE'
                              ? 'bg-white dark:bg-dark-card text-purple-700 dark:text-purple-300 shadow-sm'
                              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                          }`}
                        >
                          <Store className="w-4 h-4 flex-shrink-0" />
                          <span>Selected Store</span>
                        </button>
                      </div>
                    </div>

                    {/* Target Store Selector (if Selected Store Scope) */}
                    {upiScope === 'SINGLE' && (
                      <div>
                        <label className="block text-xs font-bold text-gray-600 dark:text-gray-300 mb-1.5">
                          Target Store <span className="text-red-500">*</span>
                        </label>
                        <select
                          id="upi-target-store-select"
                          value={upiSelectedStoreId || ''}
                          onChange={(e) => handleTargetStoreChange(Number(e.target.value))}
                          className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg text-gray-900 dark:text-white font-semibold focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition text-sm"
                        >
                          {stores.map(store => (
                            <option key={store.id} value={store.id}>
                              {store.name} {store.city ? `(${store.city})` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Helper Banner */}
                    <div className={`px-4 py-3 rounded-xl border flex items-start gap-2.5 ${
                      upiScope === 'ALL'
                        ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-300'
                        : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300'
                    }`}>
                      <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <p className="text-xs font-medium leading-relaxed">
                        {upiScope === 'ALL'
                          ? 'This configuration will be applied to all stores.'
                          : 'This configuration will be applied only to the selected store.'}
                      </p>
                    </div>

                    {/* QR Image Upload Section */}
                    <div className="flex flex-col items-center gap-3 pt-2">
                      <div className="w-32 h-32 bg-gray-100 dark:bg-dark-bg rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center overflow-hidden">
                        {upiData.upi_qr_image_url ? (
                          <img src={upiData.upi_qr_image_url} alt="UPI QR Preview" className="w-full h-full object-contain" />
                        ) : (
                          <div className="flex flex-col items-center gap-1 px-2 text-center">
                            <Upload className="w-6 h-6 text-gray-300" />
                            <span className="text-gray-400 text-xs">No QR Uploaded</span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <label className="px-4 py-2 bg-emerald-50 text-emerald-600 font-semibold rounded-xl cursor-pointer hover:bg-emerald-100 transition text-sm flex items-center gap-1.5">
                          <Upload className="w-3.5 h-3.5" />
                          {upiData.upi_qr_image_url ? 'Replace QR' : 'Upload QR'}
                          <input
                            type="file"
                            className="hidden"
                            accept="image/png,image/jpeg,image/jpg"
                            onChange={handleQrFileChange}
                          />
                        </label>
                        {upiData.upi_qr_image_url && (
                          <button
                            type="button"
                            onClick={() => setUpiData(prev => ({ ...prev, upi_qr_image_url: '' }))}
                            className="px-4 py-2 bg-red-50 text-red-600 font-semibold rounded-xl hover:bg-red-100 transition text-sm"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      {upiErrors.qrFile && (
                        <p className="text-xs text-red-500">{upiErrors.qrFile}</p>
                      )}
                      <p className="text-[11px] text-gray-400">PNG, JPG, JPEG · Max 5 MB</p>
                    </div>

                    {/* UPI ID */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 dark:text-gray-300 mb-1.5">
                        UPI ID <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="upi-id-input"
                        value={upiData.upi_id}
                        onChange={e => {
                          setUpiData(prev => ({ ...prev, upi_id: e.target.value }));
                          if (upiErrors.upi_id) setUpiErrors(prev => ({ ...prev, upi_id: undefined }));
                        }}
                        placeholder="e.g. store@okbank"
                        className={`w-full px-4 py-2.5 rounded-xl border ${
                          upiErrors.upi_id
                            ? 'border-red-400 focus:ring-red-400'
                            : 'border-gray-200 dark:border-dark-border focus:ring-emerald-500'
                        } bg-gray-50 dark:bg-dark-bg text-gray-900 dark:text-white focus:ring-2 focus:border-transparent outline-none transition`}
                      />
                      {upiErrors.upi_id && (
                        <p className="text-xs text-red-500 mt-1">{upiErrors.upi_id}</p>
                      )}
                    </div>

                    {/* Account Holder Name */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 dark:text-gray-300 mb-1.5">
                        Account Holder Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="upi-account-name-input"
                        value={upiData.account_holder_name}
                        onChange={e => {
                          setUpiData(prev => ({ ...prev, account_holder_name: e.target.value }));
                          if (upiErrors.account_holder_name) setUpiErrors(prev => ({ ...prev, account_holder_name: undefined }));
                        }}
                        placeholder="e.g. John Doe"
                        className={`w-full px-4 py-2.5 rounded-xl border ${
                          upiErrors.account_holder_name
                            ? 'border-red-400 focus:ring-red-400'
                            : 'border-gray-200 dark:border-dark-border focus:ring-emerald-500'
                        } bg-gray-50 dark:bg-dark-bg text-gray-900 dark:text-white focus:ring-2 focus:border-transparent outline-none transition`}
                      />
                      {upiErrors.account_holder_name && (
                        <p className="text-xs text-red-500 mt-1">{upiErrors.account_holder_name}</p>
                      )}
                    </div>

                    {/* Bank Name */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-600 dark:text-gray-300 mb-1.5">
                        Bank Name <span className="text-gray-400 font-normal text-xs">(Optional)</span>
                      </label>
                      <input
                        id="upi-bank-name-input"
                        value={upiData.bank_name}
                        onChange={e => setUpiData(prev => ({ ...prev, bank_name: e.target.value }))}
                        placeholder="e.g. State Bank of India"
                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition"
                      />
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center gap-3 p-6 border-t border-gray-100 dark:border-dark-border">
                    <button
                      id="upi-cancel-btn"
                      onClick={() => setShowUpiModal(false)}
                      className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-border text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-white/5 transition"
                    >
                      Cancel
                    </button>
                    <motion.button
                      id="upi-save-btn"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleSaveUpiClick}
                      disabled={savingUpi}
                      className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold shadow-sm hover:shadow-md transition disabled:opacity-50"
                    >
                      {savingUpi ? 'Saving...' : 'Save Config'}
                    </motion.button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ─── UPI QR Confirmation Dialog (All Stores / Selected Store) ─── */}
          <AnimatePresence>
            {confirmDialogType && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[110] flex items-center justify-center p-4"
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0, y: 20 }}
                  animate={{ scale: 1, opacity: 1, y: 0 }}
                  exit={{ scale: 0.9, opacity: 0, y: 20 }}
                  className="w-full max-w-sm bg-white dark:bg-dark-card rounded-2xl border border-gray-200 dark:border-dark-border shadow-2xl"
                >
                  {/* Dialog Header */}
                  <div className="p-6 pb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 flex items-center justify-center mx-auto mb-4">
                      <ShieldAlert className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white text-center">
                      {confirmDialogType === 'ALL'
                        ? 'Apply UPI QR Configuration to All Stores?'
                        : 'Apply UPI QR Configuration to This Store?'}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center mt-2 leading-relaxed">
                      {confirmDialogType === 'ALL'
                        ? 'This action will update the UPI QR code and payment details for all existing stores. Any existing store-level QR configuration will be replaced by the new configuration.'
                        : 'This action will update the UPI QR code and payment details only for the selected store.'}
                    </p>
                  </div>

                  {/* Dialog Footer */}
                  <div className="flex items-center gap-3 p-6 pt-2">
                    <button
                      id="confirm-dialog-cancel-btn"
                      onClick={() => setConfirmDialogType(null)}
                      className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-border text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-white/5 transition"
                    >
                      Cancel
                    </button>
                    <motion.button
                      id="confirm-dialog-apply-btn"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleConfirmSaveUpi}
                      className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold shadow-sm hover:shadow-md transition text-sm truncate"
                    >
                      {confirmDialogType === 'ALL' ? 'Apply to All Stores' : 'Apply to This Store'}
                    </motion.button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ─── Success Toast ─── */}
          <AnimatePresence>
            {bulkSuccessMsg && (
              <motion.div
                initial={{ opacity: 0, y: 60, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 60, scale: 0.95 }}
                transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[120] flex items-center gap-3 px-6 py-3.5 bg-white dark:bg-dark-card border border-emerald-200 dark:border-emerald-700 rounded-2xl shadow-2xl min-w-[320px] max-w-md"
              >
                <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                </div>
                <p className="text-sm font-semibold text-gray-800 dark:text-white flex-1">{bulkSuccessMsg}</p>
                <button
                  onClick={() => setBulkSuccessMsg('')}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition"
                >
                  <X className="w-4 h-4" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Stats Grid */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10"
          >
            {stats.map((stat, idx) => (
              <motion.div key={idx} variants={itemVariants}>
                <StatCard {...stat} />
              </motion.div>
            ))}
          </motion.div>

          {/* Recent Orders & Quick Actions */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
            {/* Recent Orders */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="lg:col-span-2 p-8 rounded-2xl bg-white dark:bg-dark-card border border-gray-100 dark:border-dark-border shadow-lg dark:shadow-card-dark hover:shadow-xl transition-shadow"
            >
              <div className="flex items-center justify-between gap-3 mb-6">
                <div className="flex items-center gap-3">
                  <img src="https://cdn-icons-png.flaticon.com/512/4947/4947930.png" alt="Recent Orders" className="w-8 h-8 object-contain" />
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Recent Orders</h3>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    onClick={handleViewInvoices}
                    className="text-sm text-primary font-bold hover:text-primary/80 hover:bg-primary/5 dark:hover:bg-primary/10 px-4 py-2 rounded-lg transition-all"
                  >
                    View All →
                  </motion.button>
                </div>

              </div>
              <div className="space-y-3">
                {recentOrders.length > 0 ? (
                  recentOrders.map((order, idx) => (
                    <motion.div
                      key={order.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.45 + idx * 0.1 }}
                      whileHover={{ backgroundColor: "rgba(16, 185, 129, 0.02)" }}
                      className="flex items-center justify-between p-4 rounded-xl border border-gray-100 dark:border-dark-border bg-gradient-to-r from-gray-50/50 to-transparent dark:from-white/5 dark:to-transparent hover:border-primary/20 transition-all"
                    >
                      <div className="flex-1">
                        <p className="font-bold text-gray-900 dark:text-white">{order.customer}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{order.id}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-gray-900 dark:text-white">{order.amount}</p>
                        <span
                          className={`inline-block px-3 py-1 rounded-full text-xs font-bold mt-1 ${order.status === "Completed"
                            ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300"
                            : order.status === "Pending"
                              ? "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300"
                              : "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
                            }`}
                        >
                          {order.status}
                        </span>
                      </div>
                    </motion.div>
                  ))
                ) : (
                  <p className="text-gray-600 dark:text-gray-400 text-center py-8">No recent orders</p>
                )}
              </div>
            </motion.div>

            {/* Quick Actions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="p-5 rounded-2xl bg-white dark:bg-dark-card border border-gray-100 dark:border-dark-border shadow-lg dark:shadow-card-dark hover:shadow-xl transition-shadow h-fit"
            >
              <div className="flex items-center gap-3 mb-4">
                <img src="https://cdn-icons-png.flaticon.com/512/10398/10398030.png" alt="Quick Actions" className="w-7 h-7 object-contain" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">Quick Actions</h3>
                </div>
              </div>
              <div className="space-y-2">
                <motion.button
                  onClick={handleCreateInvoice}
                  whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(16, 185, 129, 0.15)" }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-primary to-primary/80 text-white font-bold hover:shadow-xl transition-all text-base"
                >
                  Create Invoice
                </motion.button>
                <motion.button
                  onClick={handleAddProduct}
                  whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(59, 130, 246, 0.15)" }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full py-3 px-4 rounded-xl bg-secondary text-white font-bold hover:shadow-xl transition-all text-base"
                >
                  Add Product
                </motion.button>
                <motion.button
                  onClick={handleNewCustomer}
                  whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(245, 158, 11, 0.15)" }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full py-3 px-4 rounded-xl bg-accent text-white font-bold hover:shadow-xl transition-all text-base"
                >
                  New Customer
                </motion.button>
              </div>
            </motion.div>
          </div>

        </div>
      </div>

      <ExpiryOverlay
        isOpen={isExpired}
        onUpgrade={() => navigate("/subscriptions")}
      />
    </Layout>
  );
}
